import type { NavbarItem } from "@repo/ui-parkmobile/types";
import { deleteAutoSlugNavigation } from "@/utils/autoSlug";

export function isPreviewPage(pathname: string) {
  return pathname.includes("/live-preview/");
}

function normalizePath(path: string): string {
  if (!path) return "/";
  let normalized = path.trim();

  try {
    if (normalized.startsWith("http://") || normalized.startsWith("https://")) {
      const url = new URL(normalized);
      normalized = url.pathname;
    }
  } catch (e) {}

  if (!normalized.startsWith("/")) {
    normalized = "/" + normalized;
  }

  if (normalized.startsWith("/live-preview/")) {
    normalized = normalized.replace("/live-preview/", "/");
  }

  normalized = deleteAutoSlugNavigation(normalized);

  if (!normalized.startsWith("/")) {
    normalized = "/" + normalized;
  }

  if (normalized.length > 1) {
    normalized = normalized.replace(/\/$/, "");
  }

  return normalized || "/";
}

function isPathMatch(pathname: string, href: string): boolean {
  if (!pathname || !href) return false;

  try {
    if (href.startsWith("http://") || href.startsWith("https://")) {
      const url = new URL(href);
      if (typeof window !== "undefined") {
        const currentHost = window.location.hostname;
        if (url.hostname !== currentHost && url.hostname !== "localhost") {
          return false;
        }
      } else {
        return false;
      }
    }
  } catch (e) {}

  const cleanPathname = normalizePath(pathname).toLowerCase();
  const cleanHref = normalizePath(href).toLowerCase();

  if (cleanPathname === cleanHref) {
    return true;
  }

  if (cleanPathname.startsWith(cleanHref + "/")) {
    return true;
  }

  const pathnameSegments = cleanPathname.split("/").filter(Boolean);
  const hrefSegments = cleanHref.split("/").filter(Boolean);

  if (hrefSegments.length === 0 || pathnameSegments.length === 0) {
    return false;
  }

  if (hrefSegments.length === 1 && pathnameSegments.length >= 1) {
    if (pathnameSegments[0] === hrefSegments[0]) {
      return true;
    }
  }

  if (pathnameSegments.length >= hrefSegments.length) {
    let allMatch = true;
    for (let i = 0; i < hrefSegments.length; i++) {
      if (pathnameSegments[i] !== hrefSegments[i]) {
        allMatch = false;
        break;
      }
    }
    if (allMatch) {
      return true;
    }
  }

  return false;
}

export function getActiveTopLevelItemIndex(
  items: NavbarItem[],
  pathname: string,
): string | null {
  if (!pathname || !items.length) return null;

  for (const item of items) {
    if (!item.hasDropdown && item.href) {
      if (isPathMatch(pathname, item.href)) {
        return item.id;
      }
    }
  }

  for (const item of items) {
    if (item.hasDropdown && item.columns) {
      for (const column of item.columns) {
        for (const subItem of column.items) {
          if (subItem.href && isPathMatch(pathname, subItem.href)) {
            return item.id;
          }

          if (
            subItem.hasSubgroup &&
            subItem.subgroupItems &&
            subItem.subgroupItems.length > 0
          ) {
            if (
              subItem.subgroupLink &&
              isPathMatch(pathname, subItem.subgroupLink)
            ) {
              return item.id;
            }

            for (const subgroupItem of subItem.subgroupItems) {
              if (
                subgroupItem.href &&
                isPathMatch(pathname, subgroupItem.href)
              ) {
                return item.id;
              }
            }
          }
        }
      }
    }
  }

  return null;
}

export function getActiveSubItemId(
  item: NavbarItem,
  pathname: string,
): string | null {
  if (!item.hasDropdown || !item.columns || !pathname) return null;

  for (const column of item.columns) {
    for (const subItem of column.items) {
      if (subItem.href && isPathMatch(pathname, subItem.href)) {
        return subItem.id;
      }

      if (subItem.hasSubgroup && subItem.subgroupItems) {
        const hasSubgroupMatch = subItem.subgroupItems.some((subgroupItem) =>
          isPathMatch(pathname, subgroupItem.href),
        );
        if (hasSubgroupMatch) {
          return subItem.id;
        }
      }
    }
  }

  return null;
}

export function getActiveSubItemIdForAllItems(
  items: NavbarItem[],
  pathname: string,
): string | null {
  for (const item of items) {
    if (item.hasDropdown) {
      const activeSubItemId = getActiveSubItemId(item, pathname);
      if (activeSubItemId) {
        return activeSubItemId;
      }
    }
  }
  return null;
}

export function getActiveSubgroupItemId(
  item: NavbarItem,
  pathname: string,
): string | null {
  if (!item.hasDropdown || !item.columns || !pathname) return null;

  for (const column of item.columns) {
    for (const subItem of column.items) {
      if (subItem.hasSubgroup && subItem.subgroupItems) {
        for (const subgroupItem of subItem.subgroupItems) {
          if (isPathMatch(pathname, subgroupItem.href)) {
            return subgroupItem.id;
          }
        }
      }
    }
  }

  return null;
}
